<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import { usePublicToken } from '@/composables/usePublicToken'
import { apiFetch } from '@/utils/api'
import type { PaymentFeedback, PaymentProvider } from './types'

// Request step (ADA-4432 etapp 7): creates the provider transaction, then polls it until it
// reaches a terminal status. Trustly returns a `payment_url` rendered in an iframe; Swish has
// none — the payer completes it in the BankID/Swish app while this waits.
//
// Polling is an explicit 3s interval rather than TanStack's refetchInterval (D8 revised): the
// PUBLIC poll takes the token as a PATH segment, and public tokens live 60 seconds, so the token
// would sit in the query key and every refresh would churn the cache into a new entry. An
// interval that resolves a fresh token per tick is both simpler and closer to what the endpoint
// actually wants. useIntervalFn stops itself on unmount.
const props = defineProps<{
  provider: PaymentProvider
  /** The canonical 20-digit OCR — never the payer's raw input. */
  ocr: string
  amount: number
  currency?: string
  mobileNumber?: string
  /** Logged-out flow: use the public endpoints and carry a public token. */
  isPublic: boolean
}>()

const emit = defineEmits<{ feedback: [PaymentFeedback] }>()

const { t } = useI18n()
const { getPublicToken } = usePublicToken()

const transactionId = ref<string | null>(null)
const paymentUrl = ref<string | null>(null)
/** Set once a terminal status arrives, so teardown knows not to cancel a finished payment. */
const settled = ref(false)
// Set on teardown: a POST still in flight at that point resolves AFTER cancelIfUnsettled()
// saw no transaction id — without this flag that transaction would stay pending server-side
// and block the next attempt through the hasActivePayment gate (the exact case D14 removes).
let unmounted = false

const { pause, resume } = useIntervalFn(
  () => {
    poll().catch(() => {
      // poll() already swallows its own errors; this satisfies the no-floating-promises rule.
    })
  },
  3000,
  { immediate: false }
)

async function start() {
  try {
    const body = {
      provider: props.provider,
      locale: 'sv_SE',
      ocr: props.ocr,
      amount: props.amount,
      currency: props.currency ?? 'SEK',
      ...(props.provider === 'swish' ? { mobile_number: props.mobileNumber } : {}),
    }
    const { data, response } = props.isPublic
      ? await apiFetch.request('post', '/public--payment-provider-requests', {
          body: { ...body, token: await getPublicToken() },
        })
      : await apiFetch.request('post', '/payment-provider--requests', { body })
    if (response.status !== 200 || data == null) {
      emit('feedback', 'failed')
      return
    }
    transactionId.value = data.id
    if (unmounted) {
      // The payer closed the dialog while the POST was in flight — the teardown hook ran
      // before the id existed, so the cancel is ours to do, and the poll must stay off.
      cancelIfUnsettled().catch(() => undefined)
      return
    }
    paymentUrl.value = data.payment_url
    resume()
  } catch {
    emit('feedback', 'failed')
  }
}

async function poll() {
  const id = transactionId.value
  if (id == null) return
  try {
    const { data, response } = props.isPublic
      ? await apiFetch.request('get', '/public--payment-provider-requests/{transaction_id}/{token}', {
          params: { path: { transaction_id: id, token: await getPublicToken() } },
        })
      : await apiFetch.request('get', '/payment-provider--requests/{transaction_id}', {
          params: { path: { transaction_id: id } },
        })
    if (response.status !== 200 || data == null) return

    if (data.status === 'paid' || data.status === 'failed' || data.status === 'canceled') {
      settled.value = true
      pause()
      emit('feedback', data.status)
    }
  } catch {
    // A single failed poll is not a failed payment — the provider may still settle it. Keep
    // polling; the payer can close the dialog, which cancels the transaction.
  }
}

/**
 * D14: a transaction the payer walked away from stays `pending` server-side and then blocks the
 * next attempt through the hasActivePayment gate. Legacy never called this endpoint — abandoned
 * payments simply lingered. Fire-and-forget: the dialog is already going away.
 */
async function cancelIfUnsettled() {
  const id = transactionId.value
  if (id == null || settled.value) return
  try {
    if (props.isPublic) {
      await apiFetch.request('delete', '/public--payment-provider-requests/{transaction_id}/{token}', {
        params: { path: { transaction_id: id, token: await getPublicToken() } },
      })
    } else {
      await apiFetch.request('delete', '/payment-provider--requests/{transaction_id}', {
        params: { path: { transaction_id: id } },
      })
    }
  } catch {
    // Best effort — the payment provider's own timeout is the backstop.
  }
}

onMounted(() => {
  start().catch(() => {
    emit('feedback', 'failed')
  })
})
onBeforeUnmount(() => {
  unmounted = true
  pause()
  // Fire-and-forget: the component is going away and cancellation is best effort.
  cancelIfUnsettled().catch(() => undefined)
})
</script>

<template>
  <v-sheet>
    <div
      v-if="paymentUrl == null"
      class="d-flex flex-column justify-center align-center fill-height py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
        class="mb-6"
      />
      <div class="text-body-large font-weight-bold px-4 text-center mb-4">
        <template v-if="transactionId == null">&nbsp;</template>
        <template v-else-if="provider === 'swish'">{{ t('common.open_swish_to_complete_payment') }}</template>
        <template v-else>{{ t('common.waiting_for_direct_payment') }}</template>
      </div>
    </div>

    <!-- Trustly's own checkout. Height is capped against the viewport so the frame is not cut
         off inside the mobile bottom sheet (D7) — legacy hard-coded 500px and did get clipped.
         mb-4 mirrors the header divider's spacing above (röktest 2026-08-07). -->
    <iframe
      v-else
      :src="paymentUrl"
      :title="t('common.pay')"
      class="payment-frame mb-4"
    />
  </v-sheet>
</template>

<style scoped>
.payment-frame {
  border: none;
  width: 100%;
  height: min(500px, 70vh);
  display: block;
}
</style>
